// components/PlanComparisonTable.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Minus } from 'lucide-react';
export const PlanComparisonTable = () => {
    const { t } = useTranslation('marketing');
    const rows = [
        {
            key: 'max_campaigns',
            feature: t('pricing.compare.features.maxCampaigns'),
            free: t('pricing.compare.maxCampaigns.free'),
            solo: t('pricing.compare.maxCampaigns.solo'),
            team: t('pricing.compare.maxCampaigns.team'),
        },
        {
            key: 'web_mobile',
            feature: t('pricing.compare.features.webMobileAccess'),
            free: true,
            solo: true,
            team: true,
        },
        {
            key: 'basic_analytics',
            feature: t('pricing.compare.features.basicAnalytics'),
            free: true,
            solo: true,
            team: true,
        },
        {
            key: 'advanced_analytics',
            feature: t('pricing.compare.features.advancedAnalytics'),
            free: false,
            solo: true,
            team: true,
        },
        {
            key: 'campaign_modes',
            feature: t('pricing.compare.features.campaignModes', 'Campaign modes'),
            free: t('pricing.compare.campaignModes.free', 'Classic'),
            solo: t('pricing.compare.campaignModes.solo', 'Classic · Showroom · Auto-store · Feedback'),
            team: t('pricing.compare.campaignModes.team', 'Classic · Showroom · Auto-store · Feedback'),
        },
        {
            key: 'themes',
            feature: t('pricing.compare.features.themes', 'Themes'),
            free: t('pricing.compare.themes.free', '15'),
            solo: t('pricing.compare.themes.solo', '35 (full catalogue)'),
            team: t('pricing.compare.themes.team', '35 (full catalogue)'),
        },
        {
            key: 'animations',
            feature: t('pricing.compare.features.animations', 'Animated backgrounds'),
            free: t('pricing.compare.animations.free', '15'),
            solo: t('pricing.compare.animations.solo', '42 + random pool'),
            team: t('pricing.compare.animations.team', '42 + random pool'),
        },
        {
            key: 'feedback_campaigns',
            feature: t('pricing.compare.features.feedbackCampaigns', 'Feedback campaigns + Telegram delivery'),
            free: false,
            solo: true,
            team: true,
        },
        {
            key: 'auto_store',
            feature: t('pricing.compare.features.autoStore', 'Auto-store routing (iOS / Android / Desktop)'),
            free: false,
            solo: true,
            team: true,
        },
        {
            key: 'ab_testing',
            feature: t('pricing.compare.features.abTesting'),
            free: false,
            solo: true,
            team: true,
        },
        {
            key: 'max_smart_link',
            feature: t('pricing.compare.features.maxSmartLinks'),
            free: t('pricing.compare.maxSmartLinks.free'),
            solo: t('pricing.compare.maxSmartLinks.solo'),
            team: t('pricing.compare.maxSmartLinks.team'),
        },
        {
            key: 'max_comparisons',
            feature: t('pricing.compare.features.maxComparisons'),
            free: t('pricing.compare.maxComparisons.free'),
            solo: t('pricing.compare.maxComparisons.solo'),
            team: t('pricing.compare.maxComparisons.team'),
        },
        {
            key: 'geolocation',
            feature: t('pricing.compare.features.geolocation', 'Geolocation analytics (city-level)'),
            free: false,
            solo: false,
            team: true,
        },
        {
            key: 'team_members',
            feature: t('pricing.compare.features.teamMembers'),
            free: t('pricing.compare.team.free'),
            solo: t('pricing.compare.team.solo'),
            team: t('pricing.compare.team.team'),
        },
        {
            key: 'export',
            feature: t('pricing.compare.features.export'),
            free: false,
            solo: false,
            team: true,
        },
        {
            key: 'export_aba',
            feature: t('pricing.compare.features.exportAba'),
            free: false,
            solo: false,
            team: true,
        },
    ];
    const renderCell = (value, plan) => {
        const isSolo = plan === 'solo';
        if (typeof value === 'boolean') {
            if (value) {
                return (<div className="flex items-center justify-center">
                        <span className={`flex h-5 w-5 items-center justify-center rounded-full ${isSolo ? 'bg-primary/15' : 'bg-muted'}`}>
                            <Check className={`h-3 w-3 ${isSolo ? 'text-primary' : 'text-foreground/60'}`} strokeWidth={3}/>
                        </span>
                    </div>);
            }
            return (<div className="flex items-center justify-center">
                    <Minus className="h-4 w-4 text-muted-foreground/30"/>
                </div>);
        }
        if (typeof value === 'string') {
            return <span className={isSolo ? 'font-semibold text-primary' : ''}>{value}</span>;
        }
        return (<div className="flex items-center justify-center">
                <Minus className="h-4 w-4 text-muted-foreground/30"/>
            </div>);
    };
    return (<section className="mt-2">
            <h2 className="mb-2 text-center text-xs font-semibold uppercase tracking-widest text-primary">
                {t('pricing.compare.title')}
            </h2>
            <p className="mb-6 text-center text-sm text-muted-foreground">See exactly what each plan includes.</p>

            <div className="overflow-x-auto rounded-2xl border bg-card shadow-sm">
                <table className="w-full border-collapse text-xs">
                    <thead>
                        <tr className="border-b">
                            <th className="px-5 py-4 text-left font-medium text-muted-foreground">
                                {t('pricing.compare.featureCol')}
                            </th>
                            <th className="px-4 py-4 text-center font-semibold text-foreground/70">
                                {t('pricing.plans.free.name')}
                            </th>
                            <th className="px-4 py-4 text-center font-semibold bg-primary/5">
                                <span className="text-primary">{t('pricing.plans.solo.name')}</span>
                                <span className="ml-1.5 inline-flex items-center rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
                                    Popular
                                </span>
                            </th>
                            <th className="px-4 py-4 text-center font-semibold text-foreground/70">
                                {t('pricing.plans.team.name')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, idx) => (<tr key={`${row.key}-${idx}`} className={`${idx !== rows.length - 1 ? 'border-b' : ''} hover:bg-muted/30 transition-colors`}>
                                <td className="px-5 py-3.5">
                                    <span className="font-medium text-foreground/80">{row.feature}</span>
                                </td>
                                <td className="px-4 py-3.5 text-center align-middle">{renderCell(row.free, 'free')}</td>
                                <td className="px-4 py-3.5 text-center align-middle bg-primary/5">
                                    {renderCell(row.solo, 'solo')}
                                </td>
                                <td className="px-4 py-3.5 text-center align-middle">{renderCell(row.team, 'team')}</td>
                            </tr>))}
                    </tbody>
                </table>
            </div>
        </section>);
};
